import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {TransactionService} from '../../services/transaction.service';
import {LoadingState} from '../../models/loading-state';
import {ErrorSummary} from '../../models/error/error-summary';
import {CreateMatchRulePostModel} from '../../models/finance/transactions/create-match-rule-post-model';
import {
    RentRuleMatchBehaviour,
    RentRuleMatchBehaviourRadioButtonDescription,
    RentRuleMatchBehaviourRadioButtonLabel,
    RentRuleMatchBehaviourReverse,
    RentRuleMatchBehaviourReverseAction
} from '../../models/finance/rent-rule-match-behaviour';
import {LoaderButtonModule} from '../loader-button/loader-button.module';
import {ErrorResponseSummaryModule} from '../error-response-summary/error-response-summary.module';
import {RadioButtonModule} from '../radio-button/radio-button.module';
import {ErrorSummaryModule} from '../error-summary/error-summary.module';
import {FormService} from '../../services/form.service';
import {TextboxModule} from '../textbox/textbox.module';
import {DropdownModule} from '../dropdown/dropdown.module';
import {DropdownOption} from '../dropdown/dropdown-option';
import {StringMatchType, StringMatchTypeReverse} from '../../models/finance/string-match-type';
import {DateTime} from 'luxon';
import {TransactionItemFilterStatuses} from '../../models/finance/transaction-item-filter-statuses';
import {TransactionsTableSortColumn} from '../../models/finance/transactions/transactions-table-sort-column';
import {SortOrder} from '../../models/sort-order';
import {BookkeepingTransactionsList} from '../../models/finance/transactions/bookkeeping-transactions-list';
import {CommonModule} from '@angular/common';
import {DateFormats} from '../../models/dates/date-formats';
import {PipesModule} from '../../pipes/pipes.module';
import {SvgIconModule} from '../svg-icon/svg-icon.module';
import {TransactionRentMatchRule} from '../../models/finance/transaction-rent-match-rule';
import {SearchableDropdownModule} from '../searchable-dropdown/searchable-dropdown.module';
import {ManageOrganisationService} from '../../services/manage-organisation.service';
import {TransactionRentMatchRulesService} from '../../services/transaction-rent-match-rules.service';
import {TransactionRentMatchRulePostPatch} from '../../models/finance/transaction-rent-match-rule-post-patch';
import {IconNames} from '../../models/icon-names';
import {
    TransactionRentMatchRulesTableSortColumn
} from '../../models/finance/transaction-rent-match-rule/transaction-rent-match-rules-table-sort-column';

@Component({
    selector: 'transaction-rule-form',
    templateUrl: './transaction-rule-form.component.html',
    standalone: true,
    imports: [
        ErrorResponseSummaryModule,
        ReactiveFormsModule,
        LoaderButtonModule,
        RadioButtonModule,
        ErrorSummaryModule,
        TextboxModule,
        DropdownModule,
        CommonModule,
        PipesModule,
        SvgIconModule,
        SearchableDropdownModule,
    ],
    host: {
        class: 'u-width--100',
    },

})
export class TransactionRuleFormComponent implements OnInit {

    constructor(
        private transactionService: TransactionService,
        private formService: FormService,
        private fb: FormBuilder,
        private manageOrganisationService: ManageOrganisationService,
        private transactionRentMatchRulesService: TransactionRentMatchRulesService) {
    }
    @Input() tenancyGuid: string;
    @Input() transactionDescription: string;
    @Input() payeeName: string;
    @Input() organisationReference: string;
    @Input() rule: TransactionRentMatchRule;
    @Input() subform: boolean;

    @Output() onAdd = new EventEmitter<string>();
    @Output() onUpdate = new EventEmitter<TransactionRentMatchRule>();
    @Output() onClose = new EventEmitter();

    formGroup: FormGroup;
    formState = new LoadingState();
    errors: ErrorSummary;
    transactionDescriptionMatchTypeOptions: DropdownOption[] = [
        new DropdownOption('Equals', StringMatchType.Equals),
        new DropdownOption('Starts with', StringMatchType.StartsWith),
        new DropdownOption('Ends with', StringMatchType.EndsWith),
    ]
    checkingMatchingTransactions = new LoadingState();
    transactionsList: BookkeepingTransactionsList | null = null;
    transactionsListSince = DateTime.now().minus({months: 6}).toISO();
    tenanciesOptions: DropdownOption[] = [];
    gettingTenancies = new LoadingState();
    tenancyRentMatchRules: TransactionRentMatchRule[] = [];
    gettingTenancyRules = new LoadingState();
    tenancyRulesTotalCount: number;
    showTenancyRules = false;
    showPayeeFormField = false;

    protected readonly RentRuleMatchBehaviour = RentRuleMatchBehaviour;
    protected readonly RentRuleMatchBehaviourRadioButtonLabel = RentRuleMatchBehaviourRadioButtonLabel;
    protected readonly RentRuleMatchBehaviourRadioButtonDescription = RentRuleMatchBehaviourRadioButtonDescription;
    protected readonly DateFormats = DateFormats;
    protected readonly DateTime = DateTime;
    protected readonly IconNames = IconNames;
    protected readonly RentRuleMatchBehaviourReverse = RentRuleMatchBehaviourReverse;
    protected readonly RentRuleMatchBehaviourReverseAction = RentRuleMatchBehaviourReverseAction;
    protected readonly StringMatchTypeReverse = StringMatchTypeReverse;

    ngOnInit(){
        this.formGroup = this.fb.group({
            tenancyGuid: [this.rule?.tenancyGuid ?? this.tenancyGuid, Validators.required],
            transactionDescriptionMatchType: [this.rule?.transactionDescriptionMatchType ?? StringMatchType.Equals, Validators.required],
            transactionDescription: [this.rule?.transactionDescription ?? this.transactionDescription, Validators.required],
            behaviour: [this.rule?.behaviour ?? null, Validators.required],
            payeeName: [this.rule?.payeeName ?? this.payeeName],
        });

        if(this.rule || (!this.rule && !this.tenancyGuid)){
            if(this.rule){
                let label = `${this.rule.tenantName}`
                label += this.rule.roomName && this.rule.roomName.toLowerCase() !== 'tenancy'
                    ? ` (${this.rule.propertyName}, ${this.rule.roomName})`
                    : ` (${this.rule.propertyName})`;
                this.tenanciesOptions.push(new DropdownOption(label, this.rule.tenancyGuid))
            }
            this.getTenancies()
        }

        if(this.rule || this.tenancyGuid){
            this.getTenancyRentMatchRule();
        }

        if(this.rule?.payeeName || this.payeeName){
            this.showPayeeFormField = true;
        }

        this.formGroup.get('tenancyGuid').valueChanges.subscribe(value => {
            if(value){
                this.getTenancyRentMatchRule();
            } else {
                this.tenancyRentMatchRules = [];
            }
        })
    }

    checkFormErrors(){
        return this.formService.checkAllControlsAreValid({
            Tenancy: this.formGroup.get('tenancyGuid'),
            'Transaction description match type': this.formGroup.get('transactionDescriptionMatchType'),
            'Transaction description': this.formGroup.get('transactionDescription'),
            Behaviour: this.formGroup.get('behaviour'),
            'Payee name': this.formGroup.get('payeeName'),
        });
    }

    generateUpdateModel(){
        const model: TransactionRentMatchRulePostPatch = {
            tenancyGuid: this.formGroup.get('tenancyGuid').value,
            transactionDescriptionMatchType: this.formGroup.get('transactionDescriptionMatchType').value,
            transactionDescription: this.formGroup.get('transactionDescription').value,
            behaviour: this.formGroup.get('behaviour').value,
            payeeName: this.formGroup.get('payeeName').value,
            entityStatus: this.rule.entityStatus,
        }
        return model;
    }

    generatePostModel(){
        const model: CreateMatchRulePostModel = {
            tenancyGuid: this.formGroup.get('tenancyGuid').value,
            transactionDescriptionMatchType: this.formGroup.get('transactionDescriptionMatchType').value,
            transactionDescription: this.formGroup.get('transactionDescription').value,
            behaviour: this.formGroup.get('behaviour').value,
            payeeName: this.formGroup.get('payeeName').value,
        }
        return model;
    }

    submit(){
        this.errors = this.checkFormErrors();
        if(this.errors) return;

        if(this.rule){
            const model = this.generateUpdateModel();
            this.formState.fromObservable(
                this.transactionRentMatchRulesService.updateRentMatchRule(
                    this.organisationReference,
                    this.rule.guid,
                    model
                )
            ).subscribe(updatedModel => {
                this.onUpdate.emit(updatedModel)
                this.close();
            });
        } else {
            const model = this.generatePostModel();
            this.formState.fromObservable(
                this.transactionRentMatchRulesService.createRule(model)
            ).subscribe(data => {
                this.onAdd.emit(data.guid);
                this.close();
            })
        }
    }

    close(){
        this.onClose.emit();
    }

    checkMatchingTransactions(){
        this.transactionsList = null;
        this.errors = this.formService.checkAllControlsAreValid({
            Tenancy: this.formGroup.get('tenancyGuid'),
            'Transaction description match type': this.formGroup.get('transactionDescriptionMatchType'),
            'Transaction description': this.formGroup.get('transactionDescription'),
        });
        if(this.errors) return;

        this.checkingMatchingTransactions.fromObservable(
            this.transactionService.getBookkeepingTransactions(
                this.transactionsListSince,
                null,
                null,
                null,
                TransactionItemFilterStatuses.ALL,
                this.formGroup.get('transactionDescriptionMatchType').value,
                this.formGroup.get('transactionDescription').value,
                this.formGroup.get('payeeName').value,
                [],
                true,
                10,
                1,
                TransactionsTableSortColumn.Date,
                SortOrder.Desc,
            )
        ).subscribe(data => {
            this.transactionsList = data;
        })
    }

    getTenancies(){
        this.gettingTenancies.fromObservable(
            this.manageOrganisationService.getAllTenantSummaries(false, false, false)
        ).subscribe(data => {
            const options = data.map(m => {
                if(this.rule && this.rule.tenancyGuid === m.tenancySummary.guid) {
                    return;
                }
                let label = `${m.displayName}`
                label += m.tenancySummary?.roomName && m.tenancySummary?.roomName.toLowerCase() !== 'tenancy'
                    ? ` (${m.tenancySummary.propertyName}, ${m.tenancySummary.roomName})`
                    : ` (${m.tenancySummary.propertyName})`;
                return new DropdownOption(label, m.tenancySummary.guid) as DropdownOption;
            });
            this.tenanciesOptions = [...this.tenanciesOptions, ...options];
        })
    }

    getTenancyRentMatchRule() {
        this.gettingTenancyRules.fromObservable(
            this.transactionRentMatchRulesService.getRentMatchRules(
                this.organisationReference,
                null,
                this.formGroup.get('tenancyGuid').value,
                false,
                null,
                10,
                1,
                TransactionRentMatchRulesTableSortColumn.Property,
                SortOrder.Asc,
            )
        ).subscribe(data => {
            if(this.rule){
                this.tenancyRentMatchRules = data.rules.filter(r => r.guid !== this.rule.guid);
                this.tenancyRulesTotalCount = data.totalCount - 1;
            } else {
                this.tenancyRentMatchRules = data.rules;
                this.tenancyRulesTotalCount = data.totalCount;
            }
        })
    }

    toggleShowTenancyRules() {
        this.showTenancyRules = !this.showTenancyRules;
    }

    toggleShowPayeeFormField(){
        this.showPayeeFormField = !this.showPayeeFormField;
    }
}
